import { supabase } from '@/lib/supabaseClient';
import {
  FeedProductRecord,
  FeedReceiptRecord,
  FeedUsageRecord,
  FeedTransferRecord,
  BodegaInventoryItem,
} from '@/types/database';
import { documentService } from '@/services/documentService';

export interface CreateFeedReceiptPayload {
  supplier_id: string;
  farm_id?: string;
  product_id: string;
  cycle_id?: string | null;
  receipt_date: string;
  quantity_bags: number;
  total_weight_kg?: number;
  unit_price: number;
  feed_markup_amount?: number;
  trucking_cost?: number;
  delivery_receipt_no?: string | null;
  invoice_no?: string | null;
  is_direct_to_cycle?: boolean;
  notes?: string | null;
}

export interface BulkFeedInvoiceItem {
  cycle_id: string;
  product_id: string;
  quantity_bags: number;
  unit_price: number;
  feed_markup_amount?: number;
  trucking_cost?: number;
  total_weight_kg?: number;
  notes?: string | null;
}

export interface CreateBulkFeedInvoicePayload {
  supplier_id: string;
  farm_id?: string;
  invoice_no?: string | null;
  delivery_receipt_no?: string | null;
  receipt_date: string;
  items: BulkFeedInvoiceItem[];
  notes?: string | null;
}

export interface CreateFeedTransferPayload {
  source_cycle_id: string;
  destination_cycle_id: string;
  feed_product_id: string;
  source_feed_receipt_id?: string | null;
  transfer_date: string;
  quantity_bags: number;
  total_weight_kg?: number;
  unit_price?: number;
  notes?: string | null;
}

export interface CycleFeedSummary {
  receiptBags: number;
  receiptCost: number;
  transfersInBags: number;
  transfersInCost: number;
  transfersOutBags: number;
  transfersOutCost: number;
  netBags: number;
  netCost: number;
}

export interface CreateFeedUsagePayload {
  receipt_id?: string | null;
  cycle_id: string;
  product_id: string;
  farm_id: string;
  production_unit_id?: string | null;
  usage_date: string;
  quantity_bags: number;
  total_weight_kg: number;
  unit_cost: number;
  notes?: string | null;
}

export const feedService = {
  /**
   * Fetches all active feed products with configured unit weights.
   */
  async getFeedProducts(): Promise<FeedProductRecord[]> {
    const { data, error } = await supabase
      .from('feed_products')
      .select(`
        id,
        product_code,
        product_name,
        feed_type,
        supplier_id,
        unit_measurement,
        unit_weight_kg,
        default_unit_price,
        notes,
        is_active,
        is_void,
        suppliers (id, supplier_name)
      `)
      .eq('is_void', false)
      .eq('is_active', true)
      .order('product_name', { ascending: true });

    if (error) {
      console.warn('[feedService.getFeedProducts] Warn:', error.message);
      return [];
    }

    return (data || []) as unknown as FeedProductRecord[];
  },

  /**
   * Fetches feed receipts with optional filters.
   */
  async getFeedReceipts(filters: {
    farmId?: string;
    cycleId?: string;
    isDirect?: boolean;
  } = {}): Promise<FeedReceiptRecord[]> {
    let allReceipts: FeedReceiptRecord[] = [];
    let from = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const to = from + pageSize - 1;
      let query = supabase
        .from('feed_receipts')
        .select(`
          *,
          suppliers (id, supplier_code, supplier_name),
          farms (id, farm_name),
          feed_products (id, product_code, product_name, feed_type, unit_weight_kg, unit_measurement),
          production_cycles (id, cycle_code, status, operation_type, growers (full_name))
        `)
        .eq('is_void', false)
        .order('receipt_date', { ascending: false })
        .range(from, to);

      if (filters.farmId) {
        query = query.eq('farm_id', filters.farmId);
      }
      if (filters.cycleId) {
        query = query.eq('cycle_id', filters.cycleId);
      }
      if (filters.isDirect !== undefined) {
        query = query.eq('is_direct_to_cycle', filters.isDirect);
      }

      const { data, error } = await query;
      if (error) {
        console.error(`[feedService.getFeedReceipts] Error [${from}..${to}]:`, error.message);
        break;
      }

      if (data && data.length > 0) {
        allReceipts = allReceipts.concat(data as unknown as FeedReceiptRecord[]);
        if (data.length < pageSize) {
          hasMore = false;
        } else {
          from += pageSize;
        }
      } else {
        hasMore = false;
      }
    }

    // Attach documents in a single bulk query (instant, no sequential loop)
    try {
      const { data: allDocs } = await supabase
        .from('documents')
        .select('*')
        .eq('entity_type', 'feed_receipts')
        .eq('is_void', false);

      const docMap = new Map<string, any[]>();
      if (allDocs) {
        for (const doc of allDocs) {
          if (!docMap.has(doc.entity_id)) {
            docMap.set(doc.entity_id, []);
          }
          docMap.get(doc.entity_id)!.push(doc);
        }
      }

      for (const receipt of allReceipts) {
        receipt.documents = docMap.get(receipt.id) || [];
      }
    } catch {
      for (const receipt of allReceipts) {
        receipt.documents = receipt.documents || [];
      }
    }

    return allReceipts;
  },

  /**
   * Fetches feed usage allocations with optional filters.
   */
  async getFeedUsage(filters: {
    cycleId?: string;
    farmId?: string;
  } = {}): Promise<FeedUsageRecord[]> {
    let query = supabase
      .from('feed_usage')
      .select(`
        *,
        feed_products (id, product_code, product_name, feed_type, unit_weight_kg, unit_measurement),
        farms (id, farm_name),
        production_cycles (id, cycle_code, status, operation_type, growers (full_name)),
        production_units (id, unit_code, unit_name),
        feed_receipts (id, receipt_number, unit_price, delivery_receipt_no, invoice_no)
      `)
      .eq('is_void', false)
      .order('usage_date', { ascending: false });

    if (filters.cycleId) {
      query = query.eq('cycle_id', filters.cycleId);
    }
    if (filters.farmId) {
      query = query.eq('farm_id', filters.farmId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[feedService.getFeedUsage] Error:', error.message);
      throw error;
    }

    return (data || []) as unknown as FeedUsageRecord[];
  },

  /**
   * Derives real-time bodega inventory from actual non-void bodega receipts minus actual non-void usage.
   * Does not rely on a secondary manually maintained balance table.
   * Shows factual quantities (bags, kg) only without arbitrary stock level thresholds.
   */
  async getBodegaInventory(farmId?: string): Promise<BodegaInventoryItem[]> {
    // 1. Fetch non-void bodega receipts (is_direct_to_cycle = false)
    let receiptsQuery = supabase
      .from('feed_receipts')
      .select('farm_id, product_id, quantity_bags, total_weight_kg, is_void')
      .eq('is_direct_to_cycle', false)
      .eq('is_void', false);

    if (farmId) {
      receiptsQuery = receiptsQuery.eq('farm_id', farmId);
    }

    // 2. Fetch non-void feed usage
    let usageQuery = supabase
      .from('feed_usage')
      .select('farm_id, product_id, quantity_bags, total_weight_kg, is_void')
      .eq('is_void', false);

    if (farmId) {
      usageQuery = usageQuery.eq('farm_id', farmId);
    }

    // 3. Fetch farms and products metadata
    const [receiptsRes, usageRes, farmsRes, productsRes] = await Promise.all([
      receiptsQuery,
      usageQuery,
      supabase.from('farms').select('id, farm_name').eq('is_void', false),
      supabase.from('feed_products').select('id, product_name, feed_type, unit_measurement, unit_weight_kg').eq('is_void', false),
    ]);

    if (receiptsRes.error) throw receiptsRes.error;
    if (usageRes.error) throw usageRes.error;

    const farmMap = new Map<string, string>();
    (farmsRes.data || []).forEach((f: any) => farmMap.set(f.id, f.farm_name));

    const productMap = new Map<string, { product_name: string; feed_type?: string | null; unit_measurement: string; unit_weight_kg?: number | null }>();
    (productsRes.data || []).forEach((p: any) => {
      productMap.set(p.id, {
        product_name: p.product_name,
        feed_type: p.feed_type,
        unit_measurement: p.unit_measurement || 'bag',
        unit_weight_kg: p.unit_weight_kg ? Number(p.unit_weight_kg) : null,
      });
    });

    // Aggregate receipts by `${farm_id}_${product_id}`
    const receiptsTotals = new Map<string, number>();
    (receiptsRes.data || []).forEach((r: any) => {
      const key = `${r.farm_id}_${r.product_id}`;
      const prev = receiptsTotals.get(key) || 0;
      receiptsTotals.set(key, prev + (Number(r.quantity_bags) || 0));
    });

    // Aggregate usage by `${farm_id}_${product_id}`
    const usageTotals = new Map<string, number>();
    (usageRes.data || []).forEach((u: any) => {
      const key = `${u.farm_id}_${u.product_id}`;
      const prev = usageTotals.get(key) || 0;
      usageTotals.set(key, prev + (Number(u.quantity_bags) || 0));
    });

    // Combine all active keys
    const allKeys = new Set<string>([...receiptsTotals.keys(), ...usageTotals.keys()]);
    const results: BodegaInventoryItem[] = [];

    for (const key of allKeys) {
      const [fId, pId] = key.split('_');
      const prod = productMap.get(pId);
      const farmName = farmMap.get(fId) || 'Unknown Farm';
      if (!prod) continue;

      const totalReceipts = receiptsTotals.get(key) || 0;
      const totalUsage = usageTotals.get(key) || 0;
      const remainingBags = totalReceipts - totalUsage;

      const totalWeightKg = (prod.unit_weight_kg !== null && prod.unit_weight_kg !== undefined)
        ? remainingBags * prod.unit_weight_kg
        : null;

      results.push({
        farm_id: fId,
        farm_name: farmName,
        product_id: pId,
        product_name: prod.product_name,
        feed_type: prod.feed_type,
        unit_measurement: prod.unit_measurement,
        unit_weight_kg: prod.unit_weight_kg,
        total_receipts_bags: totalReceipts,
        total_usage_bags: totalUsage,
        remaining_bags: remainingBags,
        total_weight_kg: totalWeightKg,
      });
    }

    // Sort by farm name, then product name
    return results.sort((a, b) => {
      const farmCmp = a.farm_name.localeCompare(b.farm_name);
      if (farmCmp !== 0) return farmCmp;
      return a.product_name.localeCompare(b.product_name);
    });
  },

  /**
   * Fetches available non-void bodega receipts for selecting as source receipt.
   */
  async getAvailableBodegaReceipts(farmId: string, productId?: string): Promise<FeedReceiptRecord[]> {
    let query = supabase
      .from('feed_receipts')
      .select(`
        id,
        receipt_number,
        receipt_date,
        quantity_bags,
        total_weight_kg,
        unit_price,
        total_cost,
        delivery_receipt_no,
        invoice_no,
        feed_products (id, product_name, unit_weight_kg)
      `)
      .eq('farm_id', farmId)
      .eq('is_direct_to_cycle', false)
      .eq('is_void', false)
      .order('receipt_date', { ascending: false });

    if (productId) {
      query = query.eq('product_id', productId);
    }

    const { data, error } = await query;
    if (error) {
      console.warn('[feedService.getAvailableBodegaReceipts] Warn:', error.message);
      return [];
    }

    return (data || []) as unknown as FeedReceiptRecord[];
  },

  /**
   * Creates a new feed delivery receipt (direct to cycle or bodega).
   * Attaches invoice/DR with rollback safety on storage/db failure.
   */
  async createFeedReceipt(
    payload: CreateFeedReceiptPayload,
    attachedFile?: File | Blob | null
  ): Promise<FeedReceiptRecord> {
    if (!payload.supplier_id) throw new Error('Supplier is required.');
    if (!payload.farm_id) throw new Error('Farm location is required.');
    if (!payload.product_id) throw new Error('Feed product is required.');
    if (!payload.receipt_date) throw new Error('Delivery date is required.');
    if (!payload.quantity_bags || payload.quantity_bags === 0) {
      throw new Error('Quantity in bags cannot be zero.');
    }
    if (payload.unit_price < 0) throw new Error('Unit price cannot be negative.');
    if (payload.is_direct_to_cycle && !payload.cycle_id) {
      throw new Error('Destination Production Cycle is required for Direct Cycle Deliveries.');
    }

    const feedCost = Number(payload.quantity_bags) * Number(payload.unit_price);
    const feedMarkup = Number(payload.feed_markup_amount) || 0;
    const truckingCost = Number(payload.trucking_cost) || 0;
    const totalCost = feedCost + feedMarkup + truckingCost;

    const receiptNumber = `FREC-${Date.now().toString().slice(-6)}`;
    const remainingBags = payload.is_direct_to_cycle ? 0 : Number(payload.quantity_bags);

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const insertPayload: any = {
      receipt_number: receiptNumber,
      supplier_id: payload.supplier_id,
      farm_id: payload.farm_id,
      product_id: payload.product_id,
      cycle_id: payload.is_direct_to_cycle ? payload.cycle_id : null,
      receipt_date: payload.receipt_date,
      quantity_bags: Number(payload.quantity_bags),
      total_weight_kg: Number(payload.total_weight_kg) || 0,
      unit_price: Number(payload.unit_price),
      feed_cost: feedCost,
      feed_markup_amount: feedMarkup,
      trucking_cost: truckingCost,
      total_cost: totalCost,
      invoice_no: payload.invoice_no?.trim() || null,
      delivery_receipt_no: payload.delivery_receipt_no?.trim() || null,
      is_direct_to_cycle: payload.is_direct_to_cycle,
      remaining_bags: remainingBags,
      notes: payload.notes?.trim() || null,
      created_by: userId,
      updated_by: userId,
      is_void: false,
    };

    const { data: receiptRecord, error: insertError } = await supabase
      .from('feed_receipts')
      .insert(insertPayload)
      .select(`
        *,
        suppliers (id, supplier_code, supplier_name),
        farms (id, farm_name),
        feed_products (id, product_code, product_name, unit_weight_kg)
      `)
      .single();

    if (insertError) {
      console.error('[feedService.createFeedReceipt] Insert error:', insertError);
      throw new Error(`Failed to create feed receipt: ${insertError.message}`);
    }

    // Attach document if provided
    if (attachedFile && receiptRecord?.id) {
      try {
        const fileName = (attachedFile as File).name || `feed_invoice_${receiptNumber}.jpg`;
        await documentService.uploadDocument({
          file: attachedFile,
          fileName,
          entityType: 'feed_receipts',
          entityId: receiptRecord.id,
          documentType: 'invoice',
          notes: `Feed delivery invoice/DR for receipt ${receiptNumber}`,
        });
      } catch (docErr: any) {
        console.error('[feedService.createFeedReceipt] Document attachment error:', docErr);
      }
    }

    return receiptRecord as unknown as FeedReceiptRecord;
  },

  /**
   * Calculates currently available non-void bodega stock for a specific farm + product (and optional receipt).
   */
  async getAvailableBodegaStock(farmId: string, productId: string, receiptId?: string): Promise<number> {
    if (receiptId) {
      const [receiptRes, usageRes] = await Promise.all([
        supabase.from('feed_receipts').select('quantity_bags').eq('id', receiptId).eq('is_void', false).maybeSingle(),
        supabase.from('feed_usage').select('quantity_bags').eq('receipt_id', receiptId).eq('is_void', false),
      ]);
      const receiptBags = Number(receiptRes.data?.quantity_bags) || 0;
      const usedBags = (usageRes.data || []).reduce((s: number, u: any) => s + (Number(u.quantity_bags) || 0), 0);
      return Math.max(0, receiptBags - usedBags);
    }

    const [receiptsRes, usageRes] = await Promise.all([
      supabase.from('feed_receipts').select('quantity_bags').eq('farm_id', farmId).eq('product_id', productId).eq('is_direct_to_cycle', false).eq('is_void', false),
      supabase.from('feed_usage').select('quantity_bags').eq('farm_id', farmId).eq('product_id', productId).eq('is_void', false),
    ]);
    const totalReceiptBags = (receiptsRes.data || []).reduce((s: number, r: any) => s + (Number(r.quantity_bags) || 0), 0);
    const totalUsedBags = (usageRes.data || []).reduce((s: number, u: any) => s + (Number(u.quantity_bags) || 0), 0);
    return Math.max(0, totalReceiptBags - totalUsedBags);
  },

  /**
   * Dispenses feed from farm bodega to a production cycle.
   * Does not invent FIFO or automated pricing; uses actual source receipt or entered unit cost.
   * Strictly enforces that usage cannot exceed available non-void bodega inventory to prevent negative stock.
   */
  async createFeedUsage(payload: CreateFeedUsagePayload): Promise<FeedUsageRecord> {
    if (!payload.cycle_id) throw new Error('Destination Production Cycle is required.');
    if (!payload.farm_id) throw new Error('Source Farm Bodega is required.');
    if (!payload.product_id) throw new Error('Feed Product is required.');
    if (!payload.usage_date) throw new Error('Usage date is required.');
    if (!payload.quantity_bags || payload.quantity_bags <= 0) {
      throw new Error('Quantity in bags must be greater than zero.');
    }
    if (payload.unit_cost < 0) throw new Error('Unit cost cannot be negative.');

    // Strict Inventory Guard: Check available non-void bodega balance to prevent negative inventory
    const availableStock = await this.getAvailableBodegaStock(
      payload.farm_id,
      payload.product_id,
      payload.receipt_id || undefined
    );

    if (payload.quantity_bags > availableStock) {
      throw new Error(
        `Cannot dispense ${payload.quantity_bags} bags. Available non-void bodega stock is only ${availableStock} bags. Negative inventory is strictly prohibited.`
      );
    }

    const totalCost = Number(payload.quantity_bags) * Number(payload.unit_cost);
    const usageNumber = `FUSE-${Date.now().toString().slice(-6)}`;

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const insertPayload: any = {
      usage_number: usageNumber,
      receipt_id: payload.receipt_id || null,
      cycle_id: payload.cycle_id,
      farm_id: payload.farm_id,
      product_id: payload.product_id,
      production_unit_id: payload.production_unit_id || null,
      usage_date: payload.usage_date,
      quantity_bags: Number(payload.quantity_bags),
      total_weight_kg: Number(payload.total_weight_kg) || 0,
      unit_cost: Number(payload.unit_cost),
      total_cost: totalCost,
      notes: payload.notes?.trim() || null,
      created_by: userId,
      updated_by: userId,
      is_void: false,
    };

    const { data: usageRecord, error: insertError } = await supabase
      .from('feed_usage')
      .insert(insertPayload)
      .select(`
        *,
        feed_products (id, product_code, product_name, unit_weight_kg),
        farms (id, farm_name),
        production_cycles (id, cycle_code)
      `)
      .single();

    if (insertError) {
      console.error('[feedService.createFeedUsage] Insert error:', insertError);
      throw new Error(`Failed to record feed usage: ${insertError.message}`);
    }

    return usageRecord as unknown as FeedUsageRecord;
  },

  /**
   * Soft-voids a feed receipt with a mandatory audit reason.
   */
  async softVoidFeedReceipt(receiptId: string, voidReason: string): Promise<void> {
    if (!voidReason || !voidReason.trim()) {
      throw new Error('Mandatory void reason is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { error } = await supabase
      .from('feed_receipts')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        voided_by: userId,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', receiptId);

    if (error) {
      throw new Error(`Failed to void feed receipt: ${error.message}`);
    }
  },

  /**
   * Soft-voids a feed usage allocation with a mandatory audit reason.
   */
  async softVoidFeedUsage(usageId: string, voidReason: string): Promise<void> {
    if (!voidReason || !voidReason.trim()) {
      throw new Error('Mandatory void reason is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { error } = await supabase
      .from('feed_usage')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        voided_by: userId,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', usageId);

    if (error) {
      throw new Error(`Failed to void feed usage: ${error.message}`);
    }
  },

  /**
   * Creates bulk supplier feed receipts under a single supplier invoice header
   * across multiple destination Production Cycles.
   */
  async createBulkFeedInvoice(
    payload: CreateBulkFeedInvoicePayload,
    attachedFile?: File | Blob | null
  ): Promise<FeedReceiptRecord[]> {
    if (!payload.supplier_id) throw new Error('Supplier is required.');
    if (!payload.receipt_date) throw new Error('Receipt / Delivery date is required.');
    if (!payload.items || payload.items.length === 0) {
      throw new Error('At least one cycle allocation line is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    // Fetch cycle metadata to resolve farm locations
    const cycleIds = Array.from(new Set(payload.items.map((it) => it.cycle_id)));
    const { data: cyclesData } = await supabase
      .from('production_cycles')
      .select('id, farm_id')
      .in('id', cycleIds);

    const cycleFarmMap = new Map<string, string>();
    (cyclesData || []).forEach((c: any) => {
      if (c.farm_id) cycleFarmMap.set(c.id, c.farm_id);
    });

    const now = Date.now();
    const createdReceipts: any[] = [];

    for (let idx = 0; idx < payload.items.length; idx++) {
      const item = payload.items[idx];
      if (!item.cycle_id) throw new Error(`Line ${idx + 1}: Destination cycle is required.`);
      if (!item.product_id) throw new Error(`Line ${idx + 1}: Feed product is required.`);
      if (!item.quantity_bags || item.quantity_bags <= 0) {
        throw new Error(`Line ${idx + 1}: Quantity in bags must be greater than zero.`);
      }
      if (item.unit_price < 0) {
        throw new Error(`Line ${idx + 1}: Unit price cannot be negative.`);
      }

      const farmId = cycleFarmMap.get(item.cycle_id) || payload.farm_id || (cyclesData && cyclesData[0]?.farm_id);
      if (!farmId) {
        throw new Error(`Line ${idx + 1}: Could not determine farm location for cycle.`);
      }

      const feedCost = Number(item.quantity_bags) * Number(item.unit_price);
      const feedMarkup = Number(item.feed_markup_amount) || 0;
      const truckingCost = Number(item.trucking_cost) || 0;
      const totalCost = feedCost + feedMarkup + truckingCost;
      const receiptNumber = `FREC-${now.toString().slice(-6)}-${idx + 1}`;

      const insertRecord: any = {
        receipt_number: receiptNumber,
        supplier_id: payload.supplier_id,
        farm_id: farmId,
        product_id: item.product_id,
        cycle_id: item.cycle_id,
        receipt_date: payload.receipt_date,
        quantity_bags: Number(item.quantity_bags),
        total_weight_kg: Number(item.total_weight_kg) || Number(item.quantity_bags) * 50,
        unit_price: Number(item.unit_price),
        feed_cost: feedCost,
        feed_markup_amount: feedMarkup,
        trucking_cost: truckingCost,
        total_cost: totalCost,
        invoice_no: payload.invoice_no?.trim() || null,
        delivery_receipt_no: payload.delivery_receipt_no?.trim() || null,
        is_direct_to_cycle: true,
        remaining_bags: 0,
        notes: item.notes?.trim() || payload.notes?.trim() || null,
        created_by: userId,
        updated_by: userId,
        is_void: false,
      };

      const { data: inserted, error: insertErr } = await supabase
        .from('feed_receipts')
        .insert(insertRecord)
        .select(`
          *,
          suppliers (id, supplier_code, supplier_name),
          farms (id, farm_name),
          feed_products (id, product_code, product_name, unit_weight_kg),
          production_cycles (id, cycle_code, growers (full_name))
        `)
        .single();

      if (insertErr || !inserted) {
        throw new Error(`Failed to create feed receipt for line ${idx + 1}: ${insertErr?.message}`);
      }

      createdReceipts.push(inserted);
    }

    // Attach invoice document to created receipts if provided
    if (attachedFile && createdReceipts.length > 0) {
      const fileName = (attachedFile as File).name || `invoice_${payload.invoice_no || now}.jpg`;
      for (const rec of createdReceipts) {
        try {
          await documentService.uploadDocument({
            file: attachedFile,
            fileName,
            entityType: 'feed_receipts',
            entityId: rec.id,
            documentType: 'invoice',
            notes: `Supplier invoice #${payload.invoice_no || 'N/A'}`,
          });
        } catch (docErr) {
          console.error('[feedService.createBulkFeedInvoice] Document upload error:', docErr);
        }
      }
    }

    return createdReceipts as FeedReceiptRecord[];
  },

  /**
   * Fetches Cycle-to-Cycle Feed Transfers with joined cycle, grower, product and source receipt details.
   */
  async getFeedTransfers(filters: {
    cycleId?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<FeedTransferRecord[]> {
    let query = supabase
      .from('feed_transfers')
      .select(`
        *,
        source_cycle:production_cycles!source_cycle_id (
          id, cycle_code, status, operation_type, growers (id, full_name)
        ),
        destination_cycle:production_cycles!destination_cycle_id (
          id, cycle_code, status, operation_type, growers (id, full_name)
        ),
        feed_products (
          id, product_code, product_name, feed_type, unit_weight_kg
        ),
        source_feed_receipt:feed_receipts!source_feed_receipt_id (
          id, receipt_number, invoice_no, delivery_receipt_no, unit_price, total_cost, quantity_bags
        )
      `)
      .eq('is_void', false)
      .order('transfer_date', { ascending: false });

    if (filters.cycleId) {
      query = query.or(`source_cycle_id.eq.${filters.cycleId},destination_cycle_id.eq.${filters.cycleId}`);
    }
    if (filters.startDate) {
      query = query.gte('transfer_date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('transfer_date', filters.endDate);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[feedService.getFeedTransfers] Error:', error.message);
      throw error;
    }

    return (data || []) as unknown as FeedTransferRecord[];
  },

  /**
   * Creates a Cycle-to-Cycle Feed Transfer preserving proportional original booked cost.
   * Total VSB feed cost remains invariant (Source deduction = Destination addition).
   */
  async createFeedTransfer(payload: CreateFeedTransferPayload): Promise<FeedTransferRecord> {
    if (!payload.source_cycle_id) throw new Error('Source Production Cycle is required.');
    if (!payload.destination_cycle_id) throw new Error('Destination Production Cycle is required.');
    if (payload.source_cycle_id === payload.destination_cycle_id) {
      throw new Error('Source and destination production cycles must be different.');
    }
    if (!payload.feed_product_id) throw new Error('Feed Product is required.');
    if (!payload.transfer_date) throw new Error('Transfer date is required.');
    if (!payload.quantity_bags || payload.quantity_bags <= 0) {
      throw new Error('Transfer quantity in bags must be greater than zero.');
    }

    // 1. Calculate available bags on source cycle for this product
    const [sourceReceiptsRes, sourceTransfersInRes, sourceTransfersOutRes] = await Promise.all([
      supabase
        .from('feed_receipts')
        .select('id, quantity_bags, total_cost, unit_price, feed_markup_amount, trucking_cost')
        .eq('cycle_id', payload.source_cycle_id)
        .eq('product_id', payload.feed_product_id)
        .eq('is_void', false),
      supabase
        .from('feed_transfers')
        .select('quantity_bags, total_transferred_cost')
        .eq('destination_cycle_id', payload.source_cycle_id)
        .eq('feed_product_id', payload.feed_product_id)
        .eq('is_void', false),
      supabase
        .from('feed_transfers')
        .select('quantity_bags, total_transferred_cost')
        .eq('source_cycle_id', payload.source_cycle_id)
        .eq('feed_product_id', payload.feed_product_id)
        .eq('is_void', false),
    ]);

    const directBags = (sourceReceiptsRes.data || []).reduce((s: number, r: any) => s + Number(r.quantity_bags || 0), 0);
    const inBags = (sourceTransfersInRes.data || []).reduce((s: number, t: any) => s + Number(t.quantity_bags || 0), 0);
    const outBags = (sourceTransfersOutRes.data || []).reduce((s: number, t: any) => s + Number(t.quantity_bags || 0), 0);
    const availableBags = directBags + inBags - outBags;

    if (payload.quantity_bags > availableBags && availableBags > 0) {
      throw new Error(
        `Cannot transfer ${payload.quantity_bags} bags. Source cycle only has ${availableBags} available bags of this product.`
      );
    }

    // 2. Resolve unit transferred cost proportionally
    let unitTransferredCost = 0;

    if (payload.source_feed_receipt_id) {
      const { data: srcRec } = await supabase
        .from('feed_receipts')
        .select('quantity_bags, total_cost')
        .eq('id', payload.source_feed_receipt_id)
        .single();

      if (srcRec && Number(srcRec.quantity_bags) > 0) {
        unitTransferredCost = Number(srcRec.total_cost) / Number(srcRec.quantity_bags);
      }
    }

    if (unitTransferredCost <= 0) {
      // Calculate weighted average unit cost from source direct receipts
      const totalDirectCost = (sourceReceiptsRes.data || []).reduce((s: number, r: any) => s + Number(r.total_cost || 0), 0);
      if (directBags > 0) {
        unitTransferredCost = totalDirectCost / directBags;
      } else if (payload.unit_price && payload.unit_price > 0) {
        unitTransferredCost = payload.unit_price;
      } else {
        // Query product default price
        const { data: prod } = await supabase
          .from('feed_products')
          .select('default_unit_price')
          .eq('id', payload.feed_product_id)
          .single();
        unitTransferredCost = Number(prod?.default_unit_price) || 0;
      }
    }

    const totalTransferredCost = Number(payload.quantity_bags) * unitTransferredCost;
    const totalWeightKg = payload.total_weight_kg || Number(payload.quantity_bags) * 50;

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const insertPayload: any = {
      source_cycle_id: payload.source_cycle_id,
      destination_cycle_id: payload.destination_cycle_id,
      feed_product_id: payload.feed_product_id,
      source_feed_receipt_id: payload.source_feed_receipt_id || null,
      transfer_date: payload.transfer_date,
      quantity_bags: Number(payload.quantity_bags),
      total_weight_kg: totalWeightKg,
      unit_price: unitTransferredCost,
      trucking_cost_per_bag: 0,
      total_transferred_cost: totalTransferredCost,
      notes: payload.notes?.trim() || null,
      created_by: userId,
      is_void: false,
    };

    const { data: transferRecord, error: insertError } = await supabase
      .from('feed_transfers')
      .insert(insertPayload)
      .select(`
        *,
        source_cycle:production_cycles!source_cycle_id (id, cycle_code, growers (full_name)),
        destination_cycle:production_cycles!destination_cycle_id (id, cycle_code, growers (full_name)),
        feed_products (id, product_code, product_name, unit_weight_kg),
        source_feed_receipt:feed_receipts!source_feed_receipt_id (id, receipt_number, invoice_no)
      `)
      .single();

    if (insertError || !transferRecord) {
      console.error('[feedService.createFeedTransfer] Insert error:', insertError);
      throw new Error(`Failed to record feed transfer: ${insertError?.message}`);
    }

    return transferRecord as unknown as FeedTransferRecord;
  },

  /**
   * Soft-voids a feed transfer with a mandatory audit reason.
   */
  async softVoidFeedTransfer(transferId: string, voidReason: string): Promise<void> {
    if (!voidReason || !voidReason.trim()) {
      throw new Error('Mandatory void reason is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { error } = await supabase
      .from('feed_transfers')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        voided_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', transferId);

    if (error) {
      throw new Error(`Failed to void feed transfer: ${error.message}`);
    }
  },

  /**
   * Aggregates complete cycle feed financial summary (Receipts + Transfers In - Transfers Out).
   */
  async getCycleFeedSummary(cycleId: string): Promise<CycleFeedSummary> {
    const [receiptsRes, transfersInRes, transfersOutRes] = await Promise.all([
      supabase
        .from('feed_receipts')
        .select('quantity_bags, total_cost')
        .eq('cycle_id', cycleId)
        .eq('is_void', false),
      supabase
        .from('feed_transfers')
        .select('quantity_bags, total_transferred_cost')
        .eq('destination_cycle_id', cycleId)
        .eq('is_void', false),
      supabase
        .from('feed_transfers')
        .select('quantity_bags, total_transferred_cost')
        .eq('source_cycle_id', cycleId)
        .eq('is_void', false),
    ]);

    const receiptBags = (receiptsRes.data || []).reduce((s: number, r: any) => s + (Number(r.quantity_bags) || 0), 0);
    const receiptCost = (receiptsRes.data || []).reduce((s: number, r: any) => s + (Number(r.total_cost) || 0), 0);

    const transfersInBags = (transfersInRes.data || []).reduce((s: number, t: any) => s + (Number(t.quantity_bags) || 0), 0);
    const transfersInCost = (transfersInRes.data || []).reduce((s: number, t: any) => s + (Number(t.total_transferred_cost) || 0), 0);

    const transfersOutBags = (transfersOutRes.data || []).reduce((s: number, t: any) => s + (Number(t.quantity_bags) || 0), 0);
    const transfersOutCost = (transfersOutRes.data || []).reduce((s: number, t: any) => s + (Number(t.total_transferred_cost) || 0), 0);

    const netBags = receiptBags + transfersInBags - transfersOutBags;
    const netCost = receiptCost + transfersInCost - transfersOutCost;

    return {
      receiptBags,
      receiptCost,
      transfersInBags,
      transfersInCost,
      transfersOutBags,
      transfersOutCost,
      netBags,
      netCost,
    };
  },
};
